import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { InstallBanner } from "@/components/pwa";
import { TooltipProvider } from "@/components/ui/tooltip";

// Pages
import Dashboard from "@/pages/Dashboard";
import Worksheets from "@/pages/Worksheets";
import WorksheetDetail from "@/pages/WorksheetDetail";
import BuilderPage from "@/pages/BuilderPage";
import ScanPage from "@/pages/ScanPage";
import ScanResult from "@/pages/ScanResult";
import Reports from "@/pages/Reports";
import Students from "@/pages/Students";
import PlannerPage from "@/pages/PlannerPage";
import ProgressPage from "@/pages/ProgressPage";
import Settings from "@/pages/Settings";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";

// Services
import { userService, type UserProfile } from "@/services/user.service";

// Store
import { useUserStore } from "@/store/userStore";

// Hooks
import { useClassSync } from "@/hooks/useClassSync";

// Layout component with navigation
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LayoutDashboard, FileText, Camera, BarChart3, LogOut, Users, Zap, AlertTriangle, Settings as SettingsIcon, CalendarDays, TrendingUp } from "lucide-react";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { Link, useLocation } from "react-router-dom";

function NavLink({ to, icon: Icon, children }: { to: string; icon: React.ElementType; children: React.ReactNode }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link to={to}>
      <Button
        variant={isActive ? "secondary" : "ghost"}
        className="w-full justify-start"
      >
        <Icon className="mr-2 h-4 w-4" />
        {children}
      </Button>
    </Link>
  );
}

function CreditsBadge() {
  const { credits, loading, fetchCredits } = useUserStore();

  useEffect(() => {
    const controller = new AbortController();
    fetchCredits(controller.signal);
    return () => controller.abort();
  }, [fetchCredits]);

  if (loading || !credits) {
    return null;
  }

  const { creditsBalance, subscriptionStatus, daysRemaining } = credits;
  const isLowCredits = creditsBalance < 5;
  const isExpired = subscriptionStatus === 'expired' || subscriptionStatus === 'cancelled';
  const isTrial = subscriptionStatus === 'trial';

  // Expired state - show upgrade button
  if (isExpired) {
    return (
      <Link to="/upgrade">
        <Button variant="default" size="sm" className="gap-1">
          <Zap className="h-3 w-3" />
          Upgrade
        </Button>
      </Link>
    );
  }

  // Low credits warning
  if (isLowCredits) {
    return (
      <Badge variant="destructive" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        {creditsBalance} scans left
      </Badge>
    );
  }

  // Trial active with days remaining
  if (isTrial && daysRemaining !== null) {
    return (
      <Badge variant="secondary" className="gap-1">
        <Zap className="h-3 w-3" />
        Trial: {daysRemaining} days left ({creditsBalance} scans)
      </Badge>
    );
  }

  // Normal credits display
  return (
    <Badge variant="outline" className="gap-1">
      <Zap className="h-3 w-3" />
      {creditsBalance} scans remaining
    </Badge>
  );
}

function Layout() {
  const { user, signOut } = useAuth();

  // Sync local classes to server when DB connects
  useClassSync();

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation - Sticky */}
      <header className="sticky top-0 z-50 border-b bg-card">
        <div className="flex h-16 items-center px-4 md:px-6">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="lg" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2 ml-8">
            <NavLink to="/" icon={LayoutDashboard}>Dashboard</NavLink>
            <NavLink to="/worksheets" icon={FileText}>Worksheets</NavLink>
            <NavLink to="/scan" icon={Camera}>Scan</NavLink>
            <NavLink to="/planner" icon={CalendarDays}>Planner</NavLink>
            <NavLink to="/progress" icon={TrendingUp}>Progress</NavLink>
            <NavLink to="/students" icon={Users}>Students</NavLink>
            <NavLink to="/reports" icon={BarChart3}>Reports</NavLink>
          </nav>

          <div className="ml-auto flex items-center gap-4">
            <CreditsBadge />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>
                      {user?.email?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">Account</p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <Link to="/settings">
                  <DropdownMenuItem>
                    <SettingsIcon className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className="md:hidden flex items-center gap-1 px-4 pb-3 overflow-x-auto">
          <NavLink to="/" icon={LayoutDashboard}>Dashboard</NavLink>
          <NavLink to="/worksheets" icon={FileText}>Worksheets</NavLink>
          <NavLink to="/scan" icon={Camera}>Scan</NavLink>
          <NavLink to="/planner" icon={CalendarDays}>Planner</NavLink>
          <NavLink to="/progress" icon={TrendingUp}>Progress</NavLink>
          <NavLink to="/students" icon={Users}>Students</NavLink>
          <NavLink to="/reports" icon={BarChart3}>Reports</NavLink>
        </nav>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>
    </div>
  );
}

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Onboarding check wrapper - redirects to onboarding if not completed
function OnboardingCheck({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const setProfile = useUserStore((s) => s.setProfile);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setCheckingOnboarding(false);
      return;
    }

    const controller = new AbortController();

    const checkOnboarding = async () => {
      try {
        const profile = await userService.getUserProfile(controller.signal);
        setUserProfile(profile);
        // Store profile in zustand store for other components
        setProfile({
          uid: profile.uid,
          email: profile.email,
          displayName: profile.displayName,
          schoolName: profile.schoolName,
          gradeLevels: profile.gradeLevels || [],
          subjects: profile.subjects || [],
          onboardingCompleted: profile.onboardingCompleted,
        });
        setOnboardingError(null);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        // SECURITY: Fail closed - if we can't verify status, show error
        // 404 means user needs onboarding, other errors need retry
        const isNotFound = err instanceof Error && err.message.includes("404");
        if (!isNotFound) {
          setOnboardingError("Unable to load your profile. Please try again.");
        }
        // For 404, userProfile stays null which triggers onboarding redirect
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboarding();

    return () => controller.abort();
  }, [user, setProfile]);  // Only re-fetch on user change

  if (checkingOnboarding) {
    return <LoadingSpinner />;
  }

  // SECURITY: Fail closed - show error state instead of granting access
  if (onboardingError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Connection Error</CardTitle>
            <CardDescription>{onboardingError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.reload()} className="w-full">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user profile doesn't exist or onboarding not completed, redirect to onboarding
  if (!userProfile?.onboardingCompleted) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Protected route wrapper - requires authentication
function ProtectedRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <OnboardingCheck>
      <Layout />
    </OnboardingCheck>
  );
}

// Protected route for onboarding page - requires auth but not onboarding check
function OnboardingRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Onboarding />;
}

// Protected route wrapper for full-screen pages (no layout)
function ProtectedFullScreenRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <OnboardingCheck>
      {children}
    </OnboardingCheck>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TooltipProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={<OnboardingRoute />} />
            {/* Builder routes - full screen, no layout */}
            <Route path="/builder" element={<ProtectedFullScreenRoute><BuilderPage /></ProtectedFullScreenRoute>} />
            <Route path="/builder/:worksheetId" element={<ProtectedFullScreenRoute><BuilderPage /></ProtectedFullScreenRoute>} />
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/worksheets" element={<Worksheets />} />
              <Route path="/worksheets/:worksheetId" element={<WorksheetDetail />} />
              <Route path="/scan" element={<ScanPage />} />
              <Route path="/scans/:scanId" element={<ScanResult />} />
              <Route path="/planner" element={<PlannerPage />} />
              <Route path="/progress" element={<ProgressPage />} />
              <Route path="/students" element={<Students />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
          </Routes>
          <Toaster />
          <InstallBanner />
        </TooltipProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
